import { forceAssert, isObject } from '../../components/common-functions';

/**
 * Performs a property by property comparison of objects and arrays. This function also allows for certain "keys" to be
 * ignored when performing this comparison; this is useful when an object contains a function within it (since comparing
 * the equality of two functions even the same ones will always return false).
 * @param item1 - The item to be compared.
 * @param item2 - The item to compare to.
 * @param {Array<string>} ignoreKeys - Allows for certain property keys to be excluded; when a key is matched true for equals is returned.
 * @returns {boolean} True if the items are completely equal to each other.
 */
export function deepEquals(item1: unknown, item2: unknown, ignoreKeys: Array<string> = []): boolean {
  if (item1 === item2) return true;

  if (item1 && item2 && isObject(item1) && isObject(item2)) {
    if (item1.constructor !== item2.constructor) return false;

    if (Array.isArray(item1)) {
      const arrLength = item1.length;
      const arr2 = forceAssert<Array<unknown>>(item2);

      if (arrLength !== arr2.length) return false;
      for (let i = arrLength; i-- !== 0; ) {
        if (!deepEquals(item1[i], arr2[i], ignoreKeys)) return false;
      }

      return true;
    }

    if (item1.valueOf !== Object.prototype.valueOf) return item1.valueOf() === item2.valueOf();

    const keys = Object.keys(item1);
    const keys2 = Object.keys(item2);
    const length = keys.length;

    if (length !== keys2.length) return false;

    for (let i = length; i-- !== 0; ) if (!keys2.includes(keys[i])) return false;

    for (let i = length; i-- !== 0; ) {
      const key = keys[i];
      if (ignoreKeys.includes(key)) continue;
      if (!deepEquals(item1[key], item2[key], ignoreKeys)) return false;
    }

    return true;
  }

  return false;
}
